import { useMutation, useQueryClient } from "@tanstack/react-query";
import { calendarQueryKeys } from "../calendar/queries";
import { BlockType } from "../../types/block/type";
import { useUpdateCalendarBlockMutationOptions } from "../calendar/mutations";
import useFindUpdateInboxQueryKey from "../../hooks/inbox/useFindUpdateInboxQueryKey";
import { inboxQueryKeys } from "../inbox/queries";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { isAxiosError } from "axios";
import useBlockCalendarSync from "../../hooks/block/useBlockCalendarSync";
import { optimisticBlockStore } from "../../services/block/optimisticBlockStore";
import { useSetRecoilState } from "recoil";
import { mobaCalendarListState } from "../../recoil/calendar/mobaCalendarListState";
import { mergeClientServerBlocks } from "../../services/optimistic-updates/block-merger";
import { useFetchUserCalendarsQuery } from "../userCalendars/queries";
import { UserCalendarAccessType } from "../../types/userCalendars/enum";

export const useUpdateBlockMutation = () => {
  const queryClient = useQueryClient();
  const { findUpdateInboxQueryKey, isSameInboxQueryKey } = useFindUpdateInboxQueryKey();
  const { convertCalendarDataUpdateBlock } = useBlockCalendarSync();
  const setMobaCalendarList = useSetRecoilState(mobaCalendarListState);
  const fetchUserCalendarsQuery = useFetchUserCalendarsQuery({
    calendarAccessType: UserCalendarAccessType.EDITABLE,
  });

  return useMutation({
    ...useUpdateCalendarBlockMutationOptions(),
    onMutate: async ({ payload, prevData }) => {
      const updateData = convertCalendarDataUpdateBlock({ ...prevData, ...payload });

      const isPrevRecurrenceBlock =
        (prevData.recurrence && prevData.recurrence?.length > 0) || Boolean(prevData.originalId);
      const isCurrentRecurrenceBlock = updateData.recurrence && updateData.recurrence?.length > 0;

      if (!isCurrentRecurrenceBlock && !isPrevRecurrenceBlock) {
        const foundCalendar = fetchUserCalendarsQuery.data?.find(
          (calendar) => calendar.id === updateData.calendarId
        );

        const primaryCalendar = fetchUserCalendarsQuery.data?.find((calendar) => calendar.primary);

        const optimisticBlockData = {
          ...updateData,
          calendar: foundCalendar ?? primaryCalendar,
          updatedAt: new Date().toISOString(),
        };

        optimisticBlockStore.add({
          id: updateData.id,
          data: optimisticBlockData,
          isNewBlock: false,
        });

        setMobaCalendarList((prev) => mergeClientServerBlocks(prev, optimisticBlockStore.getAll()));
      }

      if (!isPrevRecurrenceBlock && isCurrentRecurrenceBlock) {
        // optimisticBlockStore에 추가되어 있는 경우 삭제 필요
        // 왜냐하면 반복 로직으로 바뀌었기 때문에 낙관적 업데이트 배열에 없어야 함.

        optimisticBlockStore.remove({
          id: updateData.clientId ?? updateData.id,
        });
      }
      // 쿼리키 찾아서 같은지 비교
      const prevInboxQueryKey = findUpdateInboxQueryKey(prevData);
      const updateInboxQueryKey = findUpdateInboxQueryKey(updateData);

      // inbox에 해당하는 쿼리키가 없으면 === TASK 블록이 아니면 리턴
      if (!prevInboxQueryKey && !updateInboxQueryKey) {
        return;
      }

      const isSame = isSameInboxQueryKey(prevInboxQueryKey, updateInboxQueryKey);
      // 같으면 updateData 업데이트
      if (isSame && updateInboxQueryKey) {
        await queryClient.cancelQueries({ queryKey: updateInboxQueryKey });
        queryClient.setQueryData<BlockType[]>(updateInboxQueryKey, (oldData) => {
          if (!oldData) return [updateData];
          return oldData.map((block) => (block.id === updateData.id ? updateData : block));
        });
        return {
          prevInboxQueryKey,
          updateInboxQueryKey,
        };
      }

      // 다르면 이전 쿼리키에서는 삭제, 변경된 쿼리키에는 추가
      // 기존 inbox영역 낙관적 업데이트(블록 제거)
      if (prevInboxQueryKey) {
        await queryClient.cancelQueries({ queryKey: prevInboxQueryKey });
        queryClient.setQueryData<BlockType[]>(prevInboxQueryKey, (oldData) => {
          if (!oldData) return [];
          return oldData.filter((block) => block.id !== prevData.id);
        });
      }

      // 이동된 inbox영역 낙관적 업데이트(블록 추가)
      if (updateInboxQueryKey) {
        await queryClient.cancelQueries({ queryKey: updateInboxQueryKey });
        queryClient.setQueryData<BlockType[]>(updateInboxQueryKey, (oldData) => {
          if (!oldData) return [updateData];
          return [updateData, ...oldData];
        });
      }
      return {
        prevInboxQueryKey,
        updateInboxQueryKey,
      };
    },
    onSuccess: (data, variables, context) => {
      // 반복인 경우에만 invalidate 처리(calendar, inbox 전체)
      const isPrevRecurrenceBlock =
        (variables.prevData.recurrence && variables.prevData.recurrence?.length > 0) ||
        Boolean(variables.prevData.originalId);
      // 원래 반복이 아니였다가 반복이 되는 블록케이스
      const isCurrentRecurrenceBlock = data.recurrence && data.recurrence?.length > 0;

      const isRecurringBlock = isPrevRecurrenceBlock || isCurrentRecurrenceBlock;
      if (isRecurringBlock) {
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
        return;
      }

      // 캘린더 영역 낙관적 업데이트 처리
      debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all);

      // context가 없는 경우가 있어서 기본값 {} 필요
      const { prevInboxQueryKey, updateInboxQueryKey } = context ?? {};
      if (!prevInboxQueryKey && !updateInboxQueryKey) {
        return;
      }

      // inbox 영역 낙관적 업데이트 처리
      const isSameQueryKey = isSameInboxQueryKey(prevInboxQueryKey, updateInboxQueryKey);
      if (isSameQueryKey) {
        if (prevInboxQueryKey) {
          debounceInvalidate.enqueueInvalidate(prevInboxQueryKey);
        }
      } else {
        if (prevInboxQueryKey) {
          debounceInvalidate.enqueueInvalidate(prevInboxQueryKey);
        }
        if (updateInboxQueryKey) {
          debounceInvalidate.enqueueInvalidate(updateInboxQueryKey);
        }
      }
    },
    onError: (error, variables, context) => {
      if (isAxiosError(error)) {
        // invalidate 처리(calendar, inbox 전체)
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
      }
    },
  });
};
